import { setup, assign, MachineContext } from "xstate";
import { fromPromise } from "xstate";
import { ITag } from "types/Tag";
import ApiSurveys from "api/surveys";
import ApiTags from "api/tags";

export const TAG_COLORS = [
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#EAB308",
  "#84CC16",
  "#22C55E",
  "#10B981",
  "#14B8A6",
  "#06B6D4",
  "#0EA5E9",
  "#3B82F6",
  "#6366F1",
  "#8B5CF6",
  "#A855F7",
  "#D946EF",
  "#EC4899",
  "#F43F5E",
];

function getAvailableColorsHelper(
  tags: ITag[],
  creates: Array<{ tempId: string; label: string; color: string }>,
  deletes: Set<number>,
  updates: Map<number, { label?: string; color?: string }>
): string[] {
  const usedColors = new Set<string>();

  tags.forEach((tag) => {
    if (!deletes.has(tag.id)) {
      const updatedColor = updates.get(tag.id)?.color;
      const color = updatedColor !== undefined ? updatedColor : tag.color;
      usedColors.add(color);
    }
  });

  creates.forEach((create) => {
    usedColors.add(create.color);
  });

  const available = TAG_COLORS.filter((color) => !usedColors.has(color));

  return available.length > 0 ? available : TAG_COLORS;
}

function getRandomColor(
  tags: ITag[],
  creates: Array<{ tempId: string; label: string; color: string }>,
  deletes: Set<number>,
  updates: Map<number, { label?: string; color?: string }>
): string {
  const available = getAvailableColorsHelper(tags, creates, deletes, updates);
  return available[Math.floor(Math.random() * available.length)];
}

/** For UI: pick a random color not already used by catalog + pending edits. */
export function pickRandomAvailableTagColor(
  tags: ITag[],
  creates: Array<{ tempId: string; label: string; color: string }>,
  deletes: Set<number>,
  updates: Map<number, { label?: string; color?: string }>
): string {
  return getRandomColor(tags, creates, deletes, updates);
}

interface TagEditorContext extends MachineContext {
  surveyId: number;
  onSync?: () => void;
  onTagsCatalogChanged?: () => void;
  canDelete: boolean;
  filterQuery: string;
  pendingUpdates: Map<number, { label?: string; color?: string }>;
  pendingCreates: Array<{ tempId: string; label: string; color: string }>;
  pendingDeletes: Set<number>;
  deleteConfirmation: {
    tagId: number;
    tagLabel: string;
    surveyCount: number;
  } | null;
  deletingTagId: number | null;
  deletingTagLabel: string | null;
  emptyStateInput: string;
  emptyStateColor: string;
  surveyTagIds: number[];
  error: string | null;
  togglingTagId: number | null;
  togglingTagIsSelected: boolean | null;
  highlightedTagIndex: number | null;
}

type TagEditorEvent =
  | { type: "TOGGLE" }
  | { type: "OPEN" }
  | { type: "CLOSE" }
  | { type: "SET_FILTER"; query: string }
  | { type: "ENTER_EDIT" }
  | { type: "CANCEL" }
  | { type: "UPDATE_LABEL"; id: string | number; label: string; isNew: boolean }
  | { type: "UPDATE_COLOR"; id: string | number; color: string; isNew: boolean }
  | {
      type: "DELETE_TAG";
      id: string | number;
      isNew: boolean;
      tagLabel?: string;
      surveyCount?: number;
    }
  | { type: "CANCEL_DELETE" }
  | { type: "CONFIRM_DELETE" }
  | { type: "ADD_NEW"; color: string }
  | { type: "SET_EMPTY_STATE_INPUT"; value: string }
  | { type: "SET_EMPTY_STATE_COLOR"; color: string }
  | { type: "EMPTY_STATE_ADD"; nextEmptyStateColor: string }
  | { type: "SAVE" }
  | { type: "TOGGLE_TAG"; tag: ITag }
  | { type: "HIGHLIGHT_NEXT"; itemCount: number }
  | { type: "HIGHLIGHT_PREV"; itemCount: number };

type ToggleTagEvent = Extract<TagEditorEvent, { type: "TOGGLE_TAG" }>;

export const createTagEditorMachine = setup({
  types: {} as {
    context: TagEditorContext;
    events: TagEditorEvent;
    input: {
      surveyId: number;
      onSync?: () => void;
      onTagsCatalogChanged?: () => void;
      canDelete?: boolean;
      surveyTagIds: number[];
    };
  },
  actors: {
    toggleTag: fromPromise(
      async ({
        input,
        signal,
      }: {
        input: {
          surveyId: number;
          tag: ITag;
          isSelected: boolean;
          onSync?: () => void;
        };
        signal: AbortSignal;
      }) => {
        try {
          if (input.isSelected) {
            await ApiSurveys.removeTagFromSurvey(
              input.surveyId,
              input.tag.id,
              signal
            );
          } else {
            await ApiSurveys.assignTagToSurvey(
              input.surveyId,
              input.tag.id,
              signal
            );
          }
          return { success: true, onSync: input.onSync };
        } catch (error) {
          if (error instanceof Error && error.name === "AbortError") {
            throw new Error("Aborted");
          }
          throw error;
        }
      }
    ),
    saveChanges: fromPromise(
      async ({
        input,
        signal,
      }: {
        input: {
          surveyId: number;
          pendingUpdates: Map<number, { label?: string; color?: string }>;
          pendingCreates: Array<{
            tempId: string;
            label: string;
            color: string;
          }>;
          onSync?: () => void;
        };
        signal: AbortSignal;
      }) => {
        const createdTags: ITag[] = [];

        try {
          for (const [tagId, updates] of input.pendingUpdates) {
            if (signal.aborted) {
              throw new Error("Aborted");
            }
            await ApiTags.updateTag(tagId, updates, signal);
          }

          for (const create of input.pendingCreates) {
            if (signal.aborted) {
              throw new Error("Aborted");
            }
            if (create.label.trim()) {
              const result = await ApiTags.createTag(
                create.label,
                create.color,
                signal
              );

              if (result.data?.tag) {
                createdTags.push(result.data.tag);
              }
            }
          }

          for (const tag of createdTags) {
            if (signal.aborted) {
              throw new Error("Aborted");
            }
            await ApiSurveys.assignTagToSurvey(input.surveyId, tag.id, signal);
          }

          return { createdTags, onSync: input.onSync };
        } catch (error) {
          if (error instanceof Error && error.name === "AbortError") {
            throw new Error("Aborted");
          }
          throw error;
        }
      }
    ),
    deleteTag: fromPromise(
      async ({
        input,
        signal,
      }: {
        input: { tagId: number; onSync?: () => void };
        signal: AbortSignal;
      }) => {
        try {
          if (signal.aborted) {
            throw new Error("Aborted");
          }

          await ApiTags.deleteTag(input.tagId, signal);
          return { success: true, onSync: input.onSync };
        } catch (error) {
          if (error instanceof Error && error.name === "AbortError") {
            throw new Error("Aborted");
          }
          throw error;
        }
      }
    ),
  },
}).createMachine({
  id: "tagEditor",
  initial: "ready",
  context: ({ input }) => {
    return {
      surveyId: input.surveyId,
      onSync: input.onSync,
      onTagsCatalogChanged: input.onTagsCatalogChanged,
      canDelete: input.canDelete ?? false,
      filterQuery: "",
      pendingUpdates: new Map(),
      pendingCreates: [],
      pendingDeletes: new Set(),
      deleteConfirmation: null,
      deletingTagId: null,
      deletingTagLabel: null,
      emptyStateInput: "",
      emptyStateColor: getRandomColor([], [], new Set(), new Map()),
      surveyTagIds: [...input.surveyTagIds],
      error: null,
      togglingTagId: null,
      togglingTagIsSelected: null,
      highlightedTagIndex: null,
    };
  },
  states: {
    ready: {
      initial: "closed",
      states: {
        closed: {
          on: {
            TOGGLE: {
              target: "open.viewing",
            },
            OPEN: {
              target: "open.viewing",
            },
          },
        },
        open: {
          initial: "viewing",
          states: {
            viewing: {
              on: {
                ENTER_EDIT: { target: "editing" },
                HIGHLIGHT_NEXT: {
                  actions: assign({
                    highlightedTagIndex: ({ context, event }) => {
                      if (event.itemCount === 0) return null;
                      if (context.highlightedTagIndex === null) {
                        return 0;
                      }
                      return (
                        (context.highlightedTagIndex + 1) % event.itemCount
                      );
                    },
                  }),
                },
                HIGHLIGHT_PREV: {
                  actions: assign({
                    highlightedTagIndex: ({ context, event }) => {
                      if (event.itemCount === 0) return null;
                      if (context.highlightedTagIndex === null) {
                        return event.itemCount - 1;
                      }
                      return (
                        (context.highlightedTagIndex - 1 + event.itemCount) %
                        event.itemCount
                      );
                    },
                  }),
                },
                EMPTY_STATE_ADD: {
                  target: "editing",
                  actions: assign({
                    pendingCreates: ({ context }) => {
                      const tempId = `new-${Date.now()}-${Math.random()}`;
                      return [
                        {
                          tempId,
                          label: context.emptyStateInput,
                          color: context.emptyStateColor,
                        },
                      ];
                    },
                    emptyStateInput: "",
                    emptyStateColor: ({ event }) => event.nextEmptyStateColor,
                  }),
                },
                SET_EMPTY_STATE_INPUT: {
                  actions: assign({
                    emptyStateInput: ({ event }) => event.value,
                  }),
                },
                SET_EMPTY_STATE_COLOR: {
                  actions: assign({
                    emptyStateColor: ({ event }) => event.color,
                  }),
                },
                TOGGLE_TAG: {
                  target: "togglingTag",
                  actions: assign({
                    togglingTagId: ({ event }) =>
                      (event as ToggleTagEvent).tag.id,
                    togglingTagIsSelected: ({ context, event }) =>
                      context.surveyTagIds.includes(
                        (event as ToggleTagEvent).tag.id
                      ),
                  }),
                },
              },
            },
            togglingTag: {
              invoke: {
                id: "toggleTag",
                src: "toggleTag",
                input: ({ context, event }) => {
                  const toggleEvent = event as ToggleTagEvent;
                  const isSelected = context.surveyTagIds.includes(
                    toggleEvent.tag.id
                  );
                  return {
                    surveyId: context.surveyId,
                    tag: toggleEvent.tag,
                    isSelected,
                    onSync: context.onSync,
                  };
                },
                onDone: {
                  target: "viewing",
                  actions: [
                    assign({
                      surveyTagIds: ({ context }) => {
                        if (
                          context.togglingTagId === null ||
                          context.togglingTagIsSelected === null
                        ) {
                          return context.surveyTagIds;
                        }
                        if (context.togglingTagIsSelected) {
                          return context.surveyTagIds.filter(
                            (id) => id !== context.togglingTagId
                          );
                        } else {
                          return [
                            ...context.surveyTagIds,
                            context.togglingTagId,
                          ];
                        }
                      },
                      togglingTagId: null,
                      togglingTagIsSelected: null,
                    }),
                    ({ event }) => {
                      event.output.onSync?.();
                    },
                  ],
                },
                onError: {
                  target: "viewing",
                  actions: assign({
                    error: ({ event }) => {
                      const errorMessage =
                        (event.error as Error)?.message || String(event.error);
                      return errorMessage === "Aborted" ? null : errorMessage;
                    },
                    togglingTagId: null,
                    togglingTagIsSelected: null,
                  }),
                },
              },
            },
            editing: {
              on: {
                CANCEL: {
                  target: "viewing",
                  actions: assign({
                    pendingUpdates: new Map(),
                    pendingCreates: [],
                    pendingDeletes: new Set(),
                    deleteConfirmation: null,
                    highlightedTagIndex: null,
                  }),
                },
                HIGHLIGHT_NEXT: {
                  actions: assign({
                    highlightedTagIndex: ({ context, event }) => {
                      if (event.itemCount === 0) return null;
                      if (context.highlightedTagIndex === null) {
                        return 0;
                      }
                      return (
                        (context.highlightedTagIndex + 1) % event.itemCount
                      );
                    },
                  }),
                },
                HIGHLIGHT_PREV: {
                  actions: assign({
                    highlightedTagIndex: ({ context, event }) => {
                      if (event.itemCount === 0) return null;
                      if (context.highlightedTagIndex === null) {
                        return event.itemCount - 1;
                      }
                      return (
                        (context.highlightedTagIndex - 1 + event.itemCount) %
                        event.itemCount
                      );
                    },
                  }),
                },
                UPDATE_LABEL: {
                  actions: assign({
                    pendingUpdates: ({ context, event }) => {
                      if (event.isNew) {
                        return context.pendingUpdates;
                      }
                      const updates = new Map(context.pendingUpdates);
                      const current = updates.get(event.id as number) || {};
                      updates.set(event.id as number, {
                        ...current,
                        label: event.label,
                      });
                      return updates;
                    },
                    pendingCreates: ({ context, event }) => {
                      if (!event.isNew) {
                        return context.pendingCreates;
                      }
                      return context.pendingCreates.map((create) =>
                        create.tempId === String(event.id)
                          ? { ...create, label: event.label }
                          : create
                      );
                    },
                  }),
                },
                UPDATE_COLOR: {
                  actions: assign({
                    pendingUpdates: ({ context, event }) => {
                      if (event.isNew) {
                        return context.pendingUpdates;
                      }
                      const updates = new Map(context.pendingUpdates);
                      const current = updates.get(event.id as number) || {};
                      updates.set(event.id as number, {
                        ...current,
                        color: event.color,
                      });
                      return updates;
                    },
                    pendingCreates: ({ context, event }) => {
                      if (!event.isNew) {
                        return context.pendingCreates;
                      }
                      return context.pendingCreates.map((create) =>
                        create.tempId === String(event.id)
                          ? { ...create, color: event.color }
                          : create
                      );
                    },
                  }),
                },
                DELETE_TAG: [
                  {
                    guard: ({ event }) => event.isNew,
                    actions: assign({
                      pendingCreates: ({ context, event }) =>
                        context.pendingCreates.filter(
                          (create) => create.tempId !== String(event.id)
                        ),
                    }),
                  },
                  {
                    guard: ({ event }) =>
                      !event.isNew && event.tagLabel !== undefined,
                    actions: assign({
                      deleteConfirmation: ({ event }) => ({
                        tagId: Number(event.id),
                        tagLabel: event.tagLabel as string,
                        surveyCount: event.surveyCount ?? 0,
                      }),
                    }),
                    target: "confirmingDelete",
                  },
                ],
                ADD_NEW: {
                  actions: assign({
                    pendingCreates: ({ context, event }) => {
                      const tempId = `new-${Date.now()}-${Math.random()}`;
                      return [
                        ...context.pendingCreates,
                        {
                          tempId,
                          label: "",
                          color: event.color,
                        },
                      ];
                    },
                  }),
                },
                SAVE: {
                  guard: ({ context }) =>
                    (context.pendingUpdates.size > 0 ||
                      context.pendingCreates.length > 0) &&
                    context.pendingCreates.every(
                      (create) => create.label.trim().length > 0
                    ),
                  target: "saving",
                },
              },
            },
            confirmingDelete: {
              on: {
                CANCEL_DELETE: {
                  target: "#tagEditor.ready.open.editing",
                  actions: assign({
                    deleteConfirmation: null,
                  }),
                },
                CONFIRM_DELETE: {
                  target: "#tagEditor.ready.open.deleting",
                  actions: assign({
                    deletingTagId: ({ context }) => {
                      return context.deleteConfirmation?.tagId ?? null;
                    },
                    deletingTagLabel: ({ context }) =>
                      context.deleteConfirmation?.tagLabel ?? null,
                    pendingUpdates: ({ context }) => {
                      if (!context.deleteConfirmation)
                        return context.pendingUpdates;
                      const updates = new Map(context.pendingUpdates);
                      updates.delete(context.deleteConfirmation.tagId);
                      return updates;
                    },
                    pendingDeletes: ({ context }) => {
                      if (!context.deleteConfirmation)
                        return context.pendingDeletes;
                      return new Set([
                        ...context.pendingDeletes,
                        context.deleteConfirmation.tagId,
                      ]);
                    },
                    deleteConfirmation: null,
                  }),
                },
                CLOSE: {
                  target: "#tagEditor.ready.closed",
                  actions: assign({
                    filterQuery: "",
                    pendingUpdates: new Map(),
                    pendingCreates: [],
                    pendingDeletes: new Set(),
                    deleteConfirmation: null,
                    highlightedTagIndex: null,
                  }),
                },
              },
            },
            deleting: {
              invoke: {
                id: "deleteTag",
                src: "deleteTag",
                input: ({ context }) => {
                  if (!context.deletingTagId) {
                    throw new Error("No tag ID to delete");
                  }
                  return {
                    tagId: context.deletingTagId,
                    onSync: context.onSync,
                  };
                },
                onDone: {
                  target: "#tagEditor.ready.open.editing",
                  actions: [
                    assign({
                      deletingTagId: null,
                      deletingTagLabel: null,
                    }),
                    ({ context }) => {
                      context.onTagsCatalogChanged?.();
                    },
                    ({ event }) => {
                      event.output.onSync?.();
                    },
                  ],
                },
                onError: {
                  target: "#tagEditor.ready.open.editing",
                  actions: [
                    assign({
                      error: ({ context, event }) => {
                        const errorMessage =
                          (event.error as Error)?.message ||
                          String(event.error);
                        if (errorMessage === "Aborted") return null;

                        if (context.deletingTagLabel) {
                          return `Failed to delete tag "${context.deletingTagLabel}". Please try again.`;
                        }
                        return errorMessage;
                      },
                      pendingDeletes: ({ context }) => {
                        if (!context.deletingTagId)
                          return context.pendingDeletes;
                        const newDeletes = new Set(context.pendingDeletes);
                        newDeletes.delete(context.deletingTagId);
                        return newDeletes;
                      },
                      deletingTagId: null,
                      deletingTagLabel: null,
                    }),
                  ],
                },
              },
              on: {
                CLOSE: {
                  target: "#tagEditor.ready.closed",
                  actions: assign({
                    filterQuery: () => "",
                    pendingUpdates: () => new Map(),
                    pendingCreates: [],
                    pendingDeletes: new Set(),
                    deleteConfirmation: null,
                    highlightedTagIndex: null,
                  }),
                },
              },
            },
            saving: {
              invoke: {
                id: "saveChanges",
                src: "saveChanges",
                input: ({ context }) => ({
                  surveyId: context.surveyId,
                  pendingUpdates: context.pendingUpdates,
                  pendingCreates: context.pendingCreates,
                  onSync: context.onSync,
                }),
                onDone: {
                  target: "editing",
                  actions: [
                    assign({
                      pendingUpdates: new Map(),
                      pendingCreates: [],
                      pendingDeletes: new Set(),
                      deleteConfirmation: null,
                      surveyTagIds: ({ context, event }): number[] => {
                        const newTagIds = event.output.createdTags.map(
                          (tag: ITag) => tag.id
                        );
                        return [...context.surveyTagIds, ...newTagIds];
                      },
                    }),
                    ({ context }) => {
                      context.onTagsCatalogChanged?.();
                    },
                    ({ event }) => {
                      event.output.onSync?.();
                    },
                  ],
                },
                onError: {
                  target: "editing",
                  actions: assign({
                    error: ({ event }) => {
                      const errorMessage =
                        (event.error as Error)?.message || String(event.error);
                      return errorMessage === "Aborted" ? null : errorMessage;
                    },
                  }),
                },
              },
            },
          },
          on: {
            SET_FILTER: {
              actions: assign({
                filterQuery: ({ event }) => event.query,
                highlightedTagIndex: null,
              }),
            },
            TOGGLE: {
              target: "closed",
              actions: assign({
                filterQuery: "",
                highlightedTagIndex: null,
              }),
            },
            CLOSE: {
              target: "closed",
              actions: assign({
                filterQuery: "",
                pendingUpdates: new Map(),
                pendingCreates: [],
                pendingDeletes: new Set(),
                deleteConfirmation: null,
                highlightedTagIndex: null,
              }),
            },
          },
        },
      },
    },
  },
});
